import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { ReviewPage } from './data/review-page';

interface ReviewModalProps {
  pages: ReviewPage[];
  title: string;
  icon: string;
  iconAlt: string;
  onClose: () => void;
}

export const ReviewModal = ({ pages, title, icon, iconAlt, onClose }: ReviewModalProps) => {
  const [stack, setStack] = useState<ReviewPage[][]>([pages]);
  const [breadcrumbs, setBreadcrumbs] = useState<(string | undefined)[]>([]);

  const currentItems = stack[stack.length - 1];
  const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const canGoBack = stack.length > 1;

  const handleItemClick = (item: ReviewPage) => {
    if (item.children) {
      setStack((prev) => [...prev, item.children!]);
      setBreadcrumbs((prev) => [...prev, item.breadcrumb]);
    } else if (item.href) {
      window.open(item.href, '_blank', 'noopener noreferrer');
    }
  };

  const handleBack = () => {
    setStack((prev) => prev.slice(0, -1));
    setBreadcrumbs((prev) => prev.slice(0, -1));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal */}
      <div
        className="relative z-10 w-fit bg-white shadow-xl overflow-hidden rounded-sm ring-1 ring-[rgb(85,85,85)] text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-mr-teal">
          <h2 className="text-lg font-bold text-white font-raleway leading-6.25">{title}</h2>
          <button
            onClick={onClose}
            className="leading-none hover:opacity-40 transition-opacity"
            style={{
              fontSize: 40,
              fontWeight: 'bold',
              color: '#000',
              opacity: 0.2,
              textShadow: '0 1px 0 #fff',
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Items */}
        <div
          className={`flex flex-col gap-2.5 px-5 pb-4 w-74.5 ${currentBreadcrumb ? 'pt-5' : 'pt-7.5'}`}
        >
          {currentBreadcrumb && (
            <p className="text-base text-gray-600 mb-1 text-left">{currentBreadcrumb}</p>
          )}
          {currentItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleItemClick(item)}
              className="w-full flex items-center gap-3 pl-2.5 pr-1 py-3 text-left border-t-[3px] border-t-mr-teal bg-white hover:bg-gray-50 transition-colors overflow-hidden"
              style={{ boxShadow: 'rgb(136, 136, 136) 0px 1px 5px -1px' }}
            >
              <img src={icon} alt={iconAlt} className="w-6 h-6 shrink-0" />
              <span className="flex-1 text-gray-700 text-base font-sans leading-[22.4px]">
                {item.label}
              </span>
              {item.children && <ChevronRight className="w-5 h-5 text-[#555555] shrink-0" />}
            </button>
          ))}
        </div>

        {/* Back button */}
        {canGoBack && (
          <div className="px-5 pb-5 relative right-1.5">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-text-link hover:text-text-link-hovered text-base"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
